import { VISIT_EVIDENCE_DETAIL_CATALOGUE } from "./visitEvidenceDetailCatalogue";
import { RESOURCE_SOLUTION_DETAIL_CATALOGUE } from "./resourceSolutionDetailCatalogue";
import { REMAINING_NATIVE_DETAIL_CATALOGUE } from "./remainingNativeDetailCatalogue";
import { HOURLY_BOTTLENECK_CATALOGUE } from "./hourlyBottleneckCatalogue";
import { REPORT_NEXT_CANDIDATE_CATALOGUE } from "./reportNextCatalogue";
import { REPORT_DETAIL_REMAINDER_CATALOGUE } from "./reportDetailRemainderCatalogue";
import { ANALYTICS_NEXT_CANDIDATE_CATALOGUE } from "./analyticsNextCatalogue";
import { RESOURCE_NEXT_DETAIL_CATALOGUE } from "./resourceNextCatalogue";
import { CALENDAR_MORE_DETAIL_CATALOGUE } from "./calendarMoreDetailCatalogue";
import { ASK_CAIRE_DETAIL_CATALOGUE } from "./askCaireDetailCatalogue";
import { GENERAL_DETAIL_CATALOGUE } from "./generalDetailsCatalogue";
import { IMPORT_REPORT_CATALOGUE } from "./importReportCatalogue";
import { OPERATIONS_CATALOGUE } from "./operationsCatalogue";
import { RESOURCE_MORE_DETAIL_CATALOGUE } from "./resourceMoreDetailsCatalogue";
import { ANALYTICS_MORE_DETAIL_CATALOGUE } from "./analyticsMoreDetailsCatalogue";
import { ANALYTICS_WORKSPACE_CATALOGUE } from "./analyticsWorkspaceCatalogue";
import { CALENDAR_DETAIL_CATALOGUE } from "./calendarDetailsCatalogue";
import { RESOURCE_DETAIL_CATALOGUE } from "./resourceDetailsCatalogue";
import { ANALYTICS_DETAIL_CATALOGUE } from "./analyticsDetailsCatalogue";
import { COMPARE_DETAIL_CATALOGUE } from "./compareDetailCatalogue";
import { CAMPAIGN_PLANNING_CATALOGUE } from "./campaignPlanningCatalogue";
import { CAMPAIGN_RESOURCES_CATALOGUE } from "./campaignResourcesCatalogue";
import { CAMPAIGN_ANALYTICS_CATALOGUE } from "./campaignAnalyticsCatalogue";
import { CAMPAIGN_SETTINGS_CATALOGUE } from "./campaignSettingsCatalogue";
import { CAMPAIGN_IMPORT_OPS_CATALOGUE } from "./campaignImportOpsCatalogue";
import { CAMPAIGN_SPECIAL_DATA_CATALOGUE } from "./campaignSpecialDataCatalogue";
import { CAMPAIGN_MOBILE_CATALOGUE } from "./campaignMobileCatalogue";
import { BRAND22_CATALOGUE } from "./brand22Catalogue";
import type { ProductImageEntry } from "./types";

/**
 * The authored half of the product image library: what each image is for and
 * the words that travel with it. Files, element boxes and figures are written
 * by the capture (`captures.json`) and by approval (`approvals.json`).
 *
 * The `live` entries are the product-page stills the website published before
 * the library existed, moved here with their text unchanged; each is replaced
 * when its scenario is captured.
 */
export const PRODUCT_IMAGE_CATALOGUE: readonly ProductImageEntry[] = [
  ...REPORT_NEXT_CANDIDATE_CATALOGUE,
  ...REPORT_DETAIL_REMAINDER_CATALOGUE,
  ...HOURLY_BOTTLENECK_CATALOGUE,
  ...REMAINING_NATIVE_DETAIL_CATALOGUE,
  ...RESOURCE_SOLUTION_DETAIL_CATALOGUE,
  ...VISIT_EVIDENCE_DETAIL_CATALOGUE,
  ...ANALYTICS_NEXT_CANDIDATE_CATALOGUE,
  ...RESOURCE_NEXT_DETAIL_CATALOGUE,
  ...GENERAL_DETAIL_CATALOGUE,
  ...ASK_CAIRE_DETAIL_CATALOGUE,
  ...IMPORT_REPORT_CATALOGUE,
  ...OPERATIONS_CATALOGUE,
  ...ANALYTICS_DETAIL_CATALOGUE,
  ...COMPARE_DETAIL_CATALOGUE,
  ...CAMPAIGN_PLANNING_CATALOGUE,
  ...CAMPAIGN_RESOURCES_CATALOGUE,
  ...CAMPAIGN_ANALYTICS_CATALOGUE,
  ...CAMPAIGN_SETTINGS_CATALOGUE,
  ...CAMPAIGN_IMPORT_OPS_CATALOGUE,
  ...CAMPAIGN_SPECIAL_DATA_CATALOGUE,
  ...CAMPAIGN_MOBILE_CATALOGUE,
  ...ANALYTICS_MORE_DETAIL_CATALOGUE,
  ...ANALYTICS_WORKSPACE_CATALOGUE,
  ...RESOURCE_DETAIL_CATALOGUE,
  ...RESOURCE_MORE_DETAIL_CATALOGUE,
  ...CALENDAR_DETAIL_CATALOGUE,
  ...CALENDAR_MORE_DETAIL_CATALOGUE,
  ...BRAND22_CATALOGUE,
  {
    id: "operations.staffing-gaps",
    module: "Bemanning",
    area: "operations",
    opens: "Publicerat schema → Bemanningsluckor",
    setup: ["Rapportera en sjukdag", "Öppna panelen Bemanningsluckor"],
    scenario: "S3",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Bemanningsluckor efter en sjukanmälan, med förslag på ersättare",
        shows:
          "Se luckorna efter en sjukanmälan och granska förslaget på ersättare.",
        proves:
          "Panelen Bemanningsluckor visar pass och besök i det publicerade schemat som saknar personal. För varje lucka föreslår Caire en ersättare med tillgänglighet, kompetens och kontinuitet, och planeraren godkänner.",
      },
      en: {
        alt: "Staffing gaps after a sickness report, with a proposed replacement; Swedish interface",
        shows:
          "See the gaps after a sickness report and review the proposed replacement.",
        proves:
          "The staffing-gaps panel lists shifts and visits in the published schedule that lack staff. For each gap Caire proposes a replacement by availability, skills and continuity, and the planner approves.",
      },
    },
    use: [
      "pages/Features/Scheduling.tsx",
      "pages/Pricing.tsx",
      "pages/Solutions/PrivateHomeCare.tsx",
      "pages/Solutions/SchedulerPersona.tsx",
    ],
  },
  {
    id: "standing-data.care-plan-visits",
    module: "Kunder och besök",
    area: "standing-data",
    opens: "Resurser → Kunder → Besöksunderlag",
    setup: ["Öppna en kunds besöksunderlag"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Besöksunderlag med tillåtna tider och flexibilitet",
        shows:
          "Besökens insatsbehov och tillåtna tider ger underlag för planeringen.",
        proves:
          "I besöksunderlaget beskrivs tidsfönster och vilka besök som får flyttas. Verksamhetens regler och behörigheter styr ändringar.",
      },
      en: {
        alt: "Visit requirements with allowed times and flexibility",
        shows: "Care needs and allowed visit times inform the planning.",
        proves:
          "Visit requirements describe time windows and which visits may move. Changes follow organisational rules and permissions.",
      },
    },
    use: [],
  },
  {
    id: "solution.compare-visits",
    module: "Jämför",
    area: "solution",
    opens: "Jämför → Ändrade besök",
    setup: ["Välj två lösningar för samma period", "Öppna Ändrade besök"],
    scenario: "live",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Jämförelse av ändrade besök mellan två planeringsresultat",
        shows:
          "Jämför två planeringsresultat för samma period, besök för besök.",
        proves:
          "Jämförelsen visar besöken som ändrats mellan två resultat: genomförandeplanens tid, tidsfönstret, planerad tid och medarbetare i vänster och höger schema. Planeraren väljer vilka ändringar som ska gälla.",
      },
      en: {
        alt: "Comparison of changed visits between two planning results",
        shows:
          "Compare two planning results for the same period, visit by visit.",
        proves:
          "The comparison lists visits that changed between two results: the care-plan time, the time window, planned time and caregiver in the left and right schedules. The planner chooses which changes apply.",
      },
    },
    use: [
      "pages/Plattform.tsx",
      "pages/Solutions/Municipalities.tsx",
      "pages/Resurser/RoutePatternsVsDynamic.tsx",
    ],
  },
  {
    id: "solution.analysis-state",
    module: "Analys",
    area: "solution",
    opens: "Schema → Analys → Läget i utkastet",
    setup: [
      "Öppna schemat på fliken Analys för CAIRE-lösningen",
      "Vänta tills korten Tilldelade besök, Brukartid och Kontinuitet visar värden",
    ],
    scenario: "S1",
    visual: {
      variant: 3,
      focus: "assigned",
      markers: [
        {
          element: "assigned",
          label: {
            sv: "Tilldelade besök i den valda lösningen",
            en: "Assigned visits in the selected solution",
          },
        },
        {
          element: "clientTime",
          label: {
            sv: "Andelen arbetstid hos klienterna",
            en: "The share of working time spent with clients",
          },
        },
        {
          element: "continuity",
          label: {
            sv: "Kontinuitet för samma period",
            en: "Continuity for the same period",
          },
        },
      ],
      metrics: [
        {
          key: "assigned",
          label: { sv: "Tilldelade besök", en: "Assigned visits" },
        },
        { key: "clientTime", label: { sv: "Brukartid", en: "Client time" } },
        { key: "continuity", label: { sv: "Kontinuitet", en: "Continuity" } },
      ],
    },
    text: {
      sv: {
        alt: "Läget i utkastet: tilldelade besök, brukartid och kontinuitet för den valda lösningen",
        shows:
          "Läs lösningens tilldelning, brukartid och kontinuitet med förutsättningarna under.",
        proves:
          "Analysvyn visar den valda lösningens tre mått, tilldelade besök, brukartidsandel och kontinuitet, med förutsättningarna för körningen. Mått jämförs inom samma period och med samma definitioner.",
      },
      en: {
        alt: "The draft's current state: assigned visits, client time and continuity for the chosen solution",
        shows:
          "Read the solution's assignment, client time and continuity with its assumptions below.",
        proves:
          "The analysis view shows the chosen solution's three measures, assigned visits, client-time share and continuity, with the assumptions behind the run. Compare measures within the same period using the same definitions.",
      },
    },
    use: [
      "pages/Plattform.tsx",
      "pages/Solutions/OperationsManagerPersona.tsx",
    ],
  },
  {
    id: "schedule.week-calendar",
    module: "Kalender",
    area: "schedule",
    opens: "Planering → Kalender, vecka",
    setup: ["Öppna kalendern i veckovy"],
    scenario: "live",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Kalendervy för medarbetarnas besök",
        shows: "Besökens ordning och rutt knyter ihop arbetsdagen.",
        proves:
          "Kalendervyn visar besökens placering och medarbetarnas arbetsdag. Läs den tillsammans med planeringsresultatet och verksamhetens regler.",
      },
      en: {
        alt: "Calendar view of employees’ visits",
        shows: "Visit order and routes connect the working day.",
        proves:
          "The calendar shows visit placement and employees’ working days. Read it together with the planning result and organisational rules.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "ai.ask-caire-panel",
    module: "Fråga CAIRE",
    area: "ai",
    opens: "Fråga CAIRE-panelen",
    setup: [
      "Öppna kalendern med ett sparat DEV-resultat",
      "Ställ analysfrågan genom den typade READ_DIAGNOSTICS-ingången",
      "Invänta ett riktigt svar med källhänvisning",
    ],
    scenario: "S6",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Fråga CAIRE i plattformen",
        shows: "Skriv till Fråga CAIRE med sammanhang från arbetet.",
        proves:
          "AI-assistenten är en ingång till frågor och stöd i plattformen. Åtkomst och möjliga handlingar följer användarens behörigheter.",
      },
      en: {
        alt: "Ask CAIRE in the platform",
        shows: "Write to Caire with context from your work.",
        proves:
          "The AI assistant provides an entry point for questions and support in the platform. Access and available actions follow the user's permissions.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "mobile.employee-shift",
    module: "Medarbetarappen",
    area: "mobile",
    opens: "Medarbetarappen → Arbetsdag",
    setup: ["Logga in som medarbetare", "Öppna dagens pass"],
    scenario: "live",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Medarbetarappens arbetsdag och besök",
        shows: "Medarbetaren följer arbetsdagen i sin app.",
        proves:
          "Medarbetarappen visar arbetsdagens besök. Olika roller har olika behörigheter och får bara se den information rollen har tillgång till.",
      },
      en: {
        alt: "Employee app with the working day and visits",
        shows:
          "Employees follow their working day in the app. Interface shown in Swedish.",
        proves:
          "The employee app shows the day's visits. Different roles have different permissions and only see the information their role can access.",
      },
    },
    use: ["pages/Solutions/Caregivers.tsx"],
  },
  {
    id: "standing-data.export",
    module: "Rapporter",
    area: "standing-data",
    opens: "Rapporter → Exportera",
    setup: ["Öppna exportdialogen"],
    scenario: "live",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Export av rapport som PDF eller Excel i Caire",
        shows: "Export och import mellan Caire och ert verksamhetssystem.",
        proves:
          "Caire tar emot stödda exporter från ert verksamhetssystem och kan exportera planering och rapporter som PDF eller Excel.",
      },
      en: {
        alt: "Exporting a report as PDF or Excel in Caire",
        shows: "Import and export between Caire and your existing system.",
        proves:
          "Caire accepts supported exports from your existing system and can export planning and reports as PDF or Excel.",
      },
    },
    use: [],
  },
  {
    id: "standing-data.visit-template",
    module: "Besöksmallar",
    area: "standing-data",
    opens: "Resurser → Kund → Besöksmall",
    setup: ["Öppna en besöksmall i redigeringsläge"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Besöksmall med rekommenderad frekvens och önskade veckodagar",
        shows:
          "Lägg till besök: rekommenderad frekvens styr önskade veckodagar.",
        proves:
          "Onboarding börjar med kund och besöksmall. Rekommenderad frekvens ger föredragna veckodagar innan första schemat skapas.",
      },
      en: {
        alt: "Visit template with recommended frequency and preferred weekdays",
        shows:
          "Add a visit: recommended frequency sets preferred days of week.",
        proves:
          "Onboarding starts with the client and visit template. Recommended frequency produces preferred weekdays before the first schedule is created.",
      },
    },
    use: [],
  },
  {
    id: "solution.compare-two",
    module: "Jämför",
    area: "solution",
    opens: "Jämför → Översikt",
    setup: ["Välj två lösningar för samma schema"],
    scenario: "live",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Jämförelse av två lösningar: besökstilldelning och tidsfördelning sida vid sida",
        shows: "Jämför två lösningar för samma schema, sida vid sida.",
        proves:
          "Jämförelsen visar två lösningar för samma schema: hur många besök som tilldelas och hur den betalda tiden fördelas på besök, restid och väntan.",
      },
      en: {
        alt: "Two solutions compared: visit assignment and time mix side by side",
        shows: "Compare two solutions for the same schedule, side by side.",
        proves:
          "The comparison shows two solutions for the same schedule: how many visits are assigned and how paid time splits into visits, travel and waiting.",
      },
    },
    use: ["pages/Solutions/HomeCareChain.tsx"],
  },
  {
    id: "standing-data.data-quality",
    module: "Datakvalitet",
    area: "standing-data",
    opens: "Resurser → Datakvalitet",
    setup: ["Öppna Datakvalitet för enheten"],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Datakvalitet: grunddata med status per steg och nästa åtgärd",
        shows:
          "Se vilka uppgifter som är klara och vad som saknas innan första schemat.",
        proves:
          "Datakvalitet visar organisation, kunder och besök, personal och skift med status per steg, och vilken uppgift som behöver kompletteras härnäst.",
      },
      en: {
        alt: "Data quality: foundation data with a status per step and the next action",
        shows:
          "See which details are complete and what is missing before the first schedule.",
        proves:
          "Data quality lists organisation, clients and visits, staff and shifts with a status per step, and which item needs completing next.",
      },
    },
    use: ["pages/Plattform.tsx"],
  },
  {
    id: "planning.map.shift-route",
    module: "Planering",
    area: "solution",
    opens: "Planering → Kalender och Karta för CAIRE-lösningen",
    setup: [
      "Öppna lösningen i läget Karta",
      "Klicka på ett pass i kalendern så att medarbetarens rutt ritas",
      "Vänta tills rutten syns på kartan och besöken listas i ordning",
    ],
    scenario: "S1",
    visual: {
      variant: 1,
      markers: [
        {
          element: "calendar",
          label: {
            sv: "Det valda passet i kalendern",
            en: "The chosen shift in the calendar",
          },
        },
        {
          element: "map",
          label: {
            sv: "Passets rutt på kartan, besök för besök",
            en: "The shift's route on the map, visit by visit",
          },
        },
        {
          element: "legs",
          label: {
            sv: "Besöken i ordning med restiden mellan dem",
            en: "The visits in order with the travel time between them",
          },
        },
      ],
    },
    text: {
      sv: {
        alt: "Kalendern med ett valt pass och passets rutt ritad på kartan",
        shows:
          "Klicka på ett pass och se medarbetarens rutt på kartan, med besöken i tur och ordning.",
        proves:
          "Rutten ritas från lösningens sparade resor: samma ordning, restider och adresser som planeringen räknade med. Planeraren ser direkt om dagen hänger ihop geografiskt.",
      },
      en: {
        alt: "The calendar with a chosen shift and its route drawn on the map",
        shows:
          "Click a shift to see the employee's route on the map, with the visits in order.",
        proves:
          "The route is drawn from the solution's saved travel: the same order, travel times and addresses the planning used. The planner sees at once whether the day holds together geographically.",
      },
    },
    use: [],
  },
  {
    id: "planning.week-pulse",
    module: "Planering",
    area: "solution",
    opens: "Planering → Kalender för CAIRE-lösningen",
    setup: [
      "Öppna lösningen i kalendern",
      "Vänta tills de tre måtten i verktygsfältet visar värden",
    ],
    scenario: "S1",
    visual: {
      variant: 3,
      focus: "calendar",
      metrics: [
        {
          key: "assigned",
          label: { sv: "Tilldelade besök", en: "Assigned visits" },
        },
        { key: "clientTime", label: { sv: "Brukartid", en: "Client time" } },
        { key: "continuity", label: { sv: "Kontinuitet", en: "Continuity" } },
      ],
    },
    text: {
      sv: {
        alt: "Planeringskalendern med tilldelade besök, brukartid och kontinuitet ovanför passen",
        shows:
          "Tre mått ovanför kalendern visar hur bra planen är medan du arbetar i den.",
        proves:
          "Måtten räknas från samma lösning som kalendern visar. Tilldelade besök, brukartidsandel och kontinuitet följer med till Analys, Jämför och rapporten med samma definitioner.",
      },
      en: {
        alt: "The planning calendar with assigned visits, client time and continuity above the shifts",
        shows:
          "Three measures above the calendar show how good the plan is while you work in it.",
        proves:
          "The measures come from the same solution the calendar shows. Assigned visits, client-time share and continuity carry over to Analysis, Compare and the report with the same definitions.",
      },
    },
    use: ["pages/Plattform.tsx", "help/schedules.first-run"],
  },
  {
    id: "solution.frontier-curve",
    module: "Lösningsförslag",
    area: "solution",
    opens: "Schema → Analys → Lösningsförslag",
    setup: [
      "Skapa tre verkliga DEV-kandidater med samma efterfrågan i behovsstyrd kapacitetsplanering",
      "Behåll genererad platshållarpersonal synlig; publicera inte innan riktig personal har kopplats",
      "Vänta på sparade slutresultat",
      "Verifiera en kandidat med otilldelade besök",
      "Öppna kurvan",
    ],
    scenario: "S2",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Tre sparade lösningsförslag på kurvan över täckning och skifteffektivitet",
        shows:
          "Jämför tre sparade kapacitetsförslag innan verklig personal kopplas och schemat publiceras.",
        proves:
          "Kurvan visar sparade DEV-kandidaters tilldelning och skifteffektivitet från samma efterfrågan. Behovsstyrd planering använder genererad platshållarpersonal; verklig personal måste kopplas innan publicering. Otilldelade besök gör skillnaden i täckning synlig.",
      },
      en: {
        alt: "Three saved planning candidates on the coverage and shift-efficiency curve",
        shows:
          "Compare three saved capacity proposals before mapping real staff and publishing the schedule.",
        proves:
          "The curve shows saved DEV candidates' assignment and paid-shift efficiency for the same demand. Demand-first planning uses generated placeholder staff; real staff must be mapped before publication. Unassigned visits make the coverage difference visible.",
      },
    },
    use: ["pages/Plattform.tsx", "pages/Features/Analytics.tsx"],
  },
  {
    id: "ai.action-proposal-preview",
    module: "Fråga CAIRE — åtgärdsförslag",
    area: "ai",
    opens: "Fråga CAIRE-panelen med ett typat åtgärdsförslag",
    setup: [
      "Öppna ett sparat DEV-resultat",
      "Förbered ett typat åtgärdsförslag genom den vanliga behörighetskontrollerade ingången",
      "Visa förhandsgranskningen utan att starta en omplanering",
    ],
    scenario: "S6",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Fråga CAIRE visar ett åtgärdsförslag före godkännande",
        shows:
          "Granska förslagets omfattning, risk och väntade påverkan innan du väljer att gå vidare.",
        proves:
          "Förslaget är knutet till den valda lösningen. Förhandsgranskningen startar ingen optimering; planeraren granskar underlaget före nästa steg.",
      },
      en: {
        alt: "Ask CAIRE shows an action proposal before approval",
        shows:
          "Review the proposal's scope, risk and expected impact before choosing to proceed.",
        proves:
          "The proposal is bound to the selected solution. Previewing it starts no optimization; the planner reviews the evidence before the next step.",
      },
    },
    use: ["pages/Plattform.tsx", "pages/HelpCenter/index.tsx"],
  },
  {
    id: "standing-data.multiple-units",
    module: "Enheter",
    area: "standing-data",
    opens: "Resurser → Enheter",
    setup: [
      "Kopiera den syntetiska Nacka-enhetens personal och besöksunderlag till en andra enhet",
      "Öppna organisationens enhetslista",
    ],
    scenario: "S5",
    visual: { variant: 0 },
    text: {
      sv: {
        alt: "Organisationens enhetslista med flera enheter",
        shows:
          "Samla flera enheter i en organisation och öppna varje enhets grunddata.",
        proves:
          "Varje enhet har sin egen personal, sina kunder och sitt besöksunderlag. Enhetslistan ger en gemensam ingång utan att blanda ihop enheternas planeringsunderlag.",
      },
      en: {
        alt: "The organisation's unit list with several units",
        shows:
          "Manage several units in one organisation and open each unit's standing data.",
        proves:
          "Each unit has its own employees, clients and visit requirements. The unit list provides a shared entry point while keeping each unit's planning inputs scoped.",
      },
    },
    use: ["pages/Plattform.tsx", "help/organization.foundation"],
  },
];

const byId = new Map(PRODUCT_IMAGE_CATALOGUE.map((entry) => [entry.id, entry]));

export function productImageEntry(id: string): ProductImageEntry {
  const entry = byId.get(id);
  if (!entry) throw new Error(`Unknown product image id: ${id}`);
  return entry;
}
